import React, { useState } from 'react';
import { Button } from '../../../components/base/Button';
import { Card } from '../../../components/base/Card';

export const ContactSection: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      setSubmitStatus('success');
      setFormData({ name: '', email: '', company: '', message: '' });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus('idle'), 3000);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-t from-brand-indigo via-brand-indigo/90 to-brand-indigo relative">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand-teal/5 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-aurora-violet/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-brand-off-white mb-8 leading-tight">
            Step in. Stand with us.
            <span className="block text-transparent bg-clip-text bg-aurora-gradient">Build Whence.</span>
          </h2>
          <div className="w-32 h-1 bg-gradient-to-r from-brand-teal to-aurora-green mx-auto rounded-full mb-8"></div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Join the pioneers shaping the future of artificial general intelligence. 
            Be part of the revolution that bridges AI to AGI.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left side - Contact form */}
          <Card variant="glass" className="p-8">
            <h3 className="text-2xl font-bold text-brand-off-white mb-6">Get Early Access</h3>
            
            <form id="whence-contact" onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-brand-off-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-transparent transition-all duration-200"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-brand-off-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-transparent transition-all duration-200"
                  placeholder="Enter your email address"
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-2">
                  Company/Organization
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-brand-off-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-transparent transition-all duration-200"
                  placeholder="Your company or organization"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-brand-off-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-transparent transition-all duration-200 resize-none"
                  placeholder="Tell us about your interest in Whence..."
                />
                <div className="text-right text-sm text-gray-400 mt-1">
                  {formData.message.length}/500
                </div>
              </div>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                disabled={isSubmitting}
                className="w-full bg-brand-teal hover:bg-brand-teal/80"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Joining...
                  </>
                ) : (
                  <>
                    <span>Join the Revolution</span>
                    <i className="ri-rocket-line ml-2"></i>
                  </>
                )}
              </Button>

              {/* Status messages */}
              {submitStatus === 'success' && (
                <div className="text-center p-4 bg-aurora-green/20 border border-aurora-green/30 rounded-lg">
                  <i className="ri-check-circle-line text-aurora-green text-xl mr-2"></i>
                  <span className="text-aurora-green">Thank you! We'll be in touch soon.</span>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="text-center p-4 bg-red-500/20 border border-red-500/30 rounded-lg">
                  <i className="ri-error-warning-line text-red-400 text-xl mr-2"></i>
                  <span className="text-red-300">Something went wrong. Please try again.</span>
                </div>
              )}
            </form>
          </Card>

          {/* Right side - Contact info and vision */}
          <div className="space-y-8">
            <Card variant="glass" className="p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-brand-teal/20 rounded-lg flex items-center justify-center mr-4">
                  <i className="ri-team-line text-2xl text-brand-teal"></i>
                </div>
                <h3 className="text-2xl font-bold text-brand-off-white">Join Our Community</h3>
              </div>
              <p className="text-gray-300 leading-relaxed mb-6">
                Connect with researchers, developers, and visionaries who are building the future of AGI. 
                Get early access to Whence products and shape the development roadmap.
              </p>
              <div className="space-y-4">
                <div className="flex items-center text-gray-300">
                  <i className="ri-mail-line text-brand-teal mr-3"></i>
                  <span>Early access notifications</span>
                </div>
                <div className="flex items-center text-gray-300">
                  <i className="ri-notification-line text-brand-teal mr-3"></i>
                  <span>Product development updates</span>
                </div>
                <div className="flex items-center text-gray-300">
                  <i className="ri-user-voice-line text-brand-teal mr-3"></i>
                  <span>Direct feedback channel</span>
                </div>
              </div>
            </Card>

            <Card variant="glass" className="p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-aurora-violet/20 rounded-lg flex items-center justify-center mr-4">
                  <i className="ri-lightbulb-line text-2xl text-aurora-violet"></i>
                </div>
                <h3 className="text-2xl font-bold text-brand-off-white">The Vision</h3>
              </div>
              <p className="text-gray-300 leading-relaxed">
                We believe that the transition from AI to AGI will define the next century of human progress. 
                Whence isn't just a product—it's a mission to create intelligence systems that truly understand, 
                remember, and evolve alongside us.
              </p>
            </Card>

            <Card variant="gradient" className="p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-brand-teal/30 rounded-full flex items-center justify-center animate-glow">
                <i className="ri-infinity-line text-2xl text-brand-teal"></i>
              </div>
              <h4 className="text-xl font-bold text-brand-off-white mb-2">The Future Starts Now</h4>
              <p className="text-gray-300 text-sm">
                Be part of the intelligence revolution
              </p>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
